import React, { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { Router } from 'react-router-dom';

import { AuthProvider } from './contexts/auth';
import { LayoutProvider } from './contexts/layout';

import Routes from './routes';
import history from './services/history';
import api from './services/api';
import applyFavicon from './functions/applyFavicon';

import GlobalStyle from './styles/global';
import { safeGet, safeSet } from './functions/safeStorage';
import { extractLayout, extractCompanies } from './functions/layoutPayload';

// O portal só renderiza depois de resolver o layout. Quando o host não tem
// layout cadastrado (a API responde 200 com corpo vazio) ou a chamada falha,
// seguimos com este objeto para não travar a tela de login: as cores e logos
// caem nos padrões de styles/global.js.
const FALLBACK_LAYOUT = {};

function App() {
  const location = window.location.host;
  const [layout, setLayout] = useState(null);

  // Ponto único de saída: aplica o favicon do cliente e libera o render.
  // `resolved` pode ser null (host sem layout, API fora do ar), mas o estado
  // nunca recebe undefined — o render abaixo devolveria undefined e o React
  // lançaria o invariant #152.
  const resolve = resolved => {
    applyFavicon(resolved && resolved.logoCircle);
    setLayout(resolved || FALLBACK_LAYOUT);
  };

  const mount = async () => {
    const cached = safeGet(localStorage, '@portal-cliente/layout');
    if (cached) {
      resolve(cached);
      return;
    }

    let data;
    try {
      const resp = await api.get(`/customer/portal/layout/${location}`);
      data = resp.data;
    } catch {
      // Rede/API fora do ar: renderiza com o padrão em vez de tela em branco.
      resolve(null);
      return;
    }

    // Só persistimos o que de fato veio. Gravar um valor ausente escreveria a
    // string "undefined" no localStorage e todo carregamento seguinte quebraria
    // no JSON.parse — inclusive depois de o backend voltar ao normal.
    const layoutFromApi = extractLayout(data);
    const companiesFromApi = extractCompanies(data);

    if (layoutFromApi) {
      safeSet(localStorage, '@portal-cliente/layout', layoutFromApi);
    }
    if (companiesFromApi) {
      safeSet(localStorage, '@portal-cliente/companies', companiesFromApi);
    }

    resolve(layoutFromApi);
  };

  useEffect(() => {
    mount();
  }, []);

  return (
    layout && (
      <LayoutProvider>
        <AuthProvider>
          <Router history={history}>
            <ToastContainer autoClose={300} />
            <GlobalStyle />
            <Routes />
          </Router>
        </AuthProvider>
      </LayoutProvider>
    )
  );
}

export default App;
